import { ComponentPropsWithoutRef, Fragment, RefObject, useEffect, useMemo, useRef } from 'react'
import { Observer, observer } from 'mobx-react-lite'
import { autorun } from 'mobx'
import NewMessageIcon from '@ui-components-3/icons/lib/regular/Message'
import cx from '@ui-components-3/ui/lib/utils/classNames'
import Message from './Message'
import VisibilityObserver from './VisibilityObserver'
import type { Merge } from '@ui-components-3/ui/lib/types'
import type { ChatMessage } from 'models/Provider/visits/PendingVisit/Chat'

/* 
    TODO:
        add types after typing models
*/

type MessagesListProps = Merge<
  ComponentPropsWithoutRef<'div'>,
  {
    visit: any
  }
>

const FAKE_STRING_FOR_TRACKING = '*'

const useRewindToEnd = (visit: any, msgListRef: RefObject<HTMLElement>) =>
  useEffect(() => {
    if (visit) {
      return autorun(
        () => {
          const { length, messages } = visit.chat

          if (length && msgListRef.current) {
            // !== FAKE_STRING_FOR_TRACKING is always truthy here, we just need to read viewedAt value to track it
            if (messages[length - 1].viewedAt !== FAKE_STRING_FOR_TRACKING) {
              msgListRef.current.scrollTop = msgListRef.current.scrollHeight
            }
          }
        },
        { delay: 256 },
      )
    }
  }, [visit])

const useVisibilityObserver = (msgListRef: RefObject<HTMLElement>) => {
  const model = (useRef<VisibilityObserver>().current ||= new VisibilityObserver())

  useEffect(() => model.start(msgListRef.current), [])

  return model
}

const MessagesList = ({ visit, className, ...props }: MessagesListProps) => {
  const msgListRef = useRef<HTMLDivElement>(null)

  useRewindToEnd(visit, msgListRef)

  const visibilityObserver = useVisibilityObserver(msgListRef)

  const { memberExtendedData, isSuccess } = visit || {}

  const memberDisplayName = useMemo(() => {
    const { firstName, lastName, firstNamePreferred } = memberExtendedData?.privateData?.member || {}

    return firstNamePreferred || [firstName, lastName].filter(Boolean).join(' ') || (isSuccess ? 'Student' : 'Patient')
  }, [isSuccess, memberExtendedData?.privateData?.member])

  return visit ? (
    <div ref={msgListRef} className={cx('gap-xs flex-auto my-s px-m overflow-auto', className)} {...props}>
      <Observer>
        {() =>
          visit.chat.fetched && !visit.chat.length ? (
            <div className="typography-body">No messages yet</div>
          ) : (
            <>
              {(visit.chat.messages as ChatMessage[]).map((msg, i, arr) => (
                <Fragment key={msg.id}>
                  {i !== 0 && !msg.read && arr[i - 1].read ? (
                    <div className="p-xs border-b border-neutral-800 font-semibold text-neutral-700 flex gap-xs mb-s justify-center items-center">
                      <NewMessageIcon aria-hidden="true" />
                      NEW MESSAGE
                    </div>
                  ) : null}
                  {i === 0 || arr[i - 1].createdDate !== msg.createdDate ? (
                    <div className="text-center mb-xxs typography-small text-neutral-500">{msg.createdDate}</div>
                  ) : null}
                  <Message
                    key={msg.id}
                    msg={msg}
                    displayName={msg.my ? 'Me' : memberDisplayName}
                    ref={(el) => visibilityObserver.observeMessage(msg, el)}
                  />
                </Fragment>
              ))}
            </>
          )
        }
      </Observer>
    </div>
  ) : null
}

export default observer(MessagesList)
